import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Trash, Eye, Save, X, Upload, Image, Loader2, Layout, ChevronDown, ChevronUp, Copy, Check, Search, SpellCheck, RefreshCw } from 'lucide-react';
import axios from 'axios';

const ArticlesManager = () => {
  const [articles, setArticles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingContent, setUploadingContent] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [copiedTemplate, setCopiedTemplate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [cohortFilter, setCohortFilter] = useState('BATCH-LI17-v1');
  const [copiedId, setCopiedId] = useState(null);
  const [spellErrors, setSpellErrors] = useState([]);
  const [checkingSpell, setCheckingSpell] = useState(false);
  const [spellScore, setSpellScore] = useState(null);
  const [manualFix, setManualFix] = useState({});
  const heroImageInputRef = useRef(null);
  const contentImageInputRef = useRef(null);
  const backendUrl = '';

  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    slug: '',
    category: 'Spirituality',
    brand: 'artenergi',
    tags: '',
    knowledge_type: 'insight',
    author: 'Keeper Of The Fiyah',
    date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    readTime: '5 min read',
    image: '',
    excerpt: '',
    content: ''
  });

  useEffect(() => {
    fetchArticles();
    fetchTemplates();
  }, []);

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/admin/articles`);
      setArticles(response.data);
    } catch (error) {
      console.error('Error fetching articles:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/admin/templates/`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  // Add template to content
  const addTemplateToContent = (templateName, templateContent) => {
    setFormData(prev => ({ 
      ...prev, 
      content: prev.content + '\n\n' + templateContent 
    }));
    alert(`Template "${templateName}" added to content!`);
  };

  // Copy template to clipboard
  const copyTemplateToClipboard = async (templateId, content) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedTemplate(templateId);
      setTimeout(() => setCopiedTemplate(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Upload hero image
  const handleHeroImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const uploadData = new FormData();
    uploadData.append('file', file);

    try {
      const response = await axios.post(`${backendUrl}/api/admin/upload-image`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Set the full URL for the hero image
      const imageUrl = `${backendUrl}${response.data.url}`;
      setFormData(prev => ({ ...prev, image: imageUrl }));
      alert('Image uploaded successfully!');
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Upload content image and insert into content
  const handleContentImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingContent(true);
    const uploadData = new FormData();
    uploadData.append('file', file);

    try {
      const response = await axios.post(`${backendUrl}/api/admin/upload-image`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Create image HTML and append to content
      const imageUrl = `${backendUrl}${response.data.url}`;
      const imageHtml = `\n<figure class="my-8">\n  <img src="${imageUrl}" alt="${file.name}" class="rounded-xl shadow-lg w-full" />\n  <figcaption class="text-center text-gray-500 text-sm mt-2">${file.name}</figcaption>\n</figure>\n`;
      
      setFormData(prev => ({ ...prev, content: prev.content + imageHtml }));
      alert('Image uploaded and added to content!');
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingContent(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Auto-generate slug from title
    if (name === 'title') {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');
      setFormData(prev => ({ ...prev, slug }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingArticle) {
        await axios.put(`${backendUrl}/api/admin/articles/${editingArticle.id}`, {
          ...formData,
          hasContent: true,
          published: true
        });
      } else {
        await axios.post(`${backendUrl}/api/admin/articles`, {
          ...formData,
          hasContent: true,
          published: true
        });
      }
      
      setShowForm(false);
      setEditingArticle(null);
      resetForm();
      fetchArticles();
      alert('Article saved successfully!');
    } catch (error) {
      console.error('Error saving article:', error);
      alert('Error saving article. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (article) => {
    setEditingArticle(article);
    setSpellErrors([]);
    setSpellScore(null);
    setFormData({
      title: article.title,
      subtitle: article.subtitle,
      slug: article.slug,
      category: article.category,
      brand: article.brand || 'artenergi',
      tags: article.tags || '',
      knowledge_type: article.knowledge_type || 'insight',
      author: article.author,
      date: article.date,
      readTime: article.readTime,
      image: article.image,
      excerpt: article.excerpt,
      content: article.content
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this article?')) {
      try {
        await axios.delete(`${backendUrl}/api/admin/articles/${id}`);
        fetchArticles();
        alert('Article deleted successfully!');
      } catch (error) {
        console.error('Error deleting article:', error);
        alert('Error deleting article.');
      }
    }
  };

  // ---- Spellcheck (Refine flow) ----
  const runSpellcheck = async () => {
    setCheckingSpell(true);
    try {
      const res = await axios.post(`${backendUrl}/api/admin/import/check-spelling`, {
        content: formData.content,
        title: formData.title,
      });
      setSpellErrors(res.data.spelling_errors || []);
      setSpellScore(res.data.spelling_score);
    } catch (err) {
      console.error('Spellcheck failed:', err);
      alert('Spellcheck failed. Please try again.');
    } finally {
      setCheckingSpell(false);
    }
  };

  const applySpellFix = (word, replacement) => {
    if (!replacement) return;
    const re = new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g');
    setFormData(prev => ({ ...prev, content: prev.content.replace(re, replacement) }));
    setSpellErrors(prev => prev.filter(e => e.word.toLowerCase() !== word.toLowerCase()));
  };

  const ignoreSpell = (word) => {
    setSpellErrors(prev => prev.filter(e => e.word.toLowerCase() !== word.toLowerCase()));
  };

  const addToDict = async (word) => {
    try {
      await axios.post(`${backendUrl}/api/admin/import/brand-dictionary`, { word });
      setSpellErrors(prev => prev.filter(e => e.word.toLowerCase() !== word.toLowerCase()));
    } catch (err) {
      console.error('Add to dictionary failed:', err);
    }
  };

  const copyId = async (id) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch (err) { /* noop */ }
  };

  const filteredArticles = articles.filter((a) => {
    const cohort = a.cohort || 'PRE-BATCH-RND';
    if (cohortFilter !== 'ALL' && cohort !== cohortFilter) return false;
    const q = searchTerm.trim().toLowerCase();
    if (!q) return true;
    return (
      (a.title || '').toLowerCase().includes(q) ||
      (a.slug || '').toLowerCase().includes(q) ||
      (a.id || '').toLowerCase().includes(q)
    );
  });

  const cohortCounts = articles.reduce((acc, a) => {
    const c = a.cohort || 'PRE-BATCH-RND';
    acc[c] = (acc[c] || 0) + 1;
    return acc;
  }, {});

  const resetForm = () => {
    setFormData({
      title: '',
      subtitle: '',
      slug: '',
      category: 'Spirituality',
      brand: 'artenergi',
      tags: '',
      knowledge_type: 'insight',
      author: 'Keeper Of The Fiyah',
      date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
      readTime: '5 min read',
      image: '',
      excerpt: '',
      content: ''
    });
  };

  return (
    <div>
      {/* Header with Add Button */}
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-2xl font-bold text-gray-900">
          {searchTerm ? `${filteredArticles.length} / ${articles.length}` : articles.length} Articles
        </h3>
        <button
          onClick={() => {
            setShowForm(true);
            setEditingArticle(null);
            resetForm();
          }}
          className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-brand-deep to-fire-main text-white rounded-full hover:shadow-lg transition-all"
        >
          <Plus size={20} />
          <span>Add Article</span>
        </button>
      </div>

      {/* Search + cohort filter — paste an Article ID (from the batch CSV), title, or slug */}
      {!showForm && (
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by Article ID (from CSV), title, or slug…"
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900 bg-white"
              data-testid="articles-search-input"
            />
          </div>
          <select
            value={cohortFilter}
            onChange={(e) => setCohortFilter(e.target.value)}
            className="px-3 py-2.5 border border-gray-300 rounded-lg text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            data-testid="cohort-filter"
          >
            <option value="BATCH-LI17-v1">BATCH-LI17-v1 ({cohortCounts['BATCH-LI17-v1'] || 0})</option>
            <option value="PRE-BATCH-RND">Pre-Batch R&D — legacy ({cohortCounts['PRE-BATCH-RND'] || 0})</option>
            <option value="ALL">All cohorts ({articles.length})</option>
          </select>
        </div>
      )}

      {/* Articles List */}
      {!showForm && (
        <div className="space-y-3">
          {filteredArticles.map((article) => (
            <div key={article.id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow flex">
              <img src={article.image} alt={article.title} className="w-32 h-auto object-cover flex-shrink-0" />
              <div className="p-4 flex-1 flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`inline-block px-2 py-0.5 text-xs rounded-full font-medium ${
                      article.published ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {article.published ? 'Published' : 'Draft'}
                    </span>
                    <span className="inline-block px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                      {article.category}
                    </span>
                    {article.completeness_status && (
                      <span className={`inline-block px-2 py-0.5 text-xs rounded-full font-medium ${
                        article.completeness_status === 'complete' ? 'bg-green-100 text-green-700'
                        : article.completeness_status === 'partial' ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-red-100 text-red-700'}`}>
                        {article.completeness_status}
                      </span>
                    )}
                    <span className={`inline-block px-2 py-0.5 text-xs rounded-full font-medium ${
                      (article.cohort || 'PRE-BATCH-RND') === 'BATCH-LI17-v1'
                        ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-200 text-gray-500'}`}
                      title="Testing cohort">
                      {(article.cohort || 'PRE-BATCH-RND') === 'BATCH-LI17-v1' ? 'BATCH-LI17-v1' : 'legacy R&D'}
                    </span>
                  </div>
                  <h4 className="font-bold text-gray-900 truncate">{article.title}</h4>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-xs text-gray-500">{article.date} | {article.readTime}</p>
                    <button
                      onClick={() => copyId(article.id)}
                      className="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-purple-600 font-mono"
                      title="Copy Article ID"
                      data-testid={`copy-id-${article.id}`}
                    >
                      {copiedId === article.id ? <Check size={11} className="text-green-500" /> : <Copy size={11} />}
                      {(article.id || '').slice(0, 8)}…
                    </button>
                  </div>
                </div>
                <div className="flex items-center space-x-2 ml-4 flex-shrink-0">
                  <a
                    href={`/article/${article.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                    title="View article"
                    data-testid={`view-article-${article.slug}`}
                  >
                    <Eye size={16} />
                    <span>View</span>
                  </a>
                  <button
                    onClick={() => handleEdit(article)}
                    className="flex items-center space-x-1 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                    data-testid={`edit-article-${article.id}`}
                  >
                    <Edit size={16} />
                    <span>Edit</span>
                  </button>
                  <button
                    onClick={() => handleDelete(article.id)}
                    className="flex items-center justify-center px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                  >
                    <Trash size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {filteredArticles.length === 0 && (
            <p className="text-center text-gray-500 py-8">No articles match “{searchTerm}”.</p>
          )}
        </div>
      )}

      {/* Article Form */}
      {showForm && (
        <div className="bg-white rounded-xl shadow-lg p-6" style={{ backgroundColor: '#ffffff' }}>
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold text-gray-900">{editingArticle ? 'Edit Article' : 'Add New Article'}</h3>
            <button
              onClick={() => {
                setShowForm(false);
                setEditingArticle(null);
              }}
              className="p-2 hover:bg-gray-100 rounded-full text-gray-700"
            >
              <X size={24} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900 bg-white"
                  style={{ color: '#111827', backgroundColor: '#ffffff' }}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">Slug (auto-generated)</label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900 bg-gray-50"
                  style={{ color: '#111827' }}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">Subtitle</label>
              <input
                type="text"
                name="subtitle"
                value={formData.subtitle}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900 bg-white"
                style={{ color: '#111827', backgroundColor: '#ffffff' }}
              />
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">Category</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900 bg-white"
                  style={{ color: '#111827', backgroundColor: '#ffffff' }}
                >
                  <option>Spirituality</option>
                  <option>Business</option>
                  <option>Wellness</option>
                  <option>Culture</option>
                  <option>Technology</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">Author</label>
                <select
                  name="author"
                  value={formData.author}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900 bg-white"
                  style={{ color: '#111827', backgroundColor: '#ffffff' }}
                >
                  <option>Keeper Of The Fiyah</option>
                  <option>FLY FREQUENCY</option>
                  <option>C to the T</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">Read Time</label>
                <input
                  type="text"
                  name="readTime"
                  value={formData.readTime}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900 bg-white"
                  style={{ color: '#111827', backgroundColor: '#ffffff' }}
                  placeholder="5 min read"
                />
              </div>
            </div>

            {/* Knowledge Classification */}
            <div className="grid md:grid-cols-3 gap-4 p-4 rounded-lg" style={{ backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb' }}>
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">Brand</label>
                <select
                  name="brand"
                  value={formData.brand}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900 bg-white"
                  style={{ color: '#111827', backgroundColor: '#ffffff' }}
                >
                  <option value="artenergi">A.R.T. Energi</option>
                  <option value="inspiyah">INSPIYAH</option>
                  <option value="culture">INSPIYAH Culture</option>
                  <option value="colox">Book of Colox</option>
                  <option value="dnfministry">DNF Ministry</option>
                  <option value="ecosystem">Ecosystem (All Brands)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">Knowledge Type</label>
                <select
                  name="knowledge_type"
                  value={formData.knowledge_type}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900 bg-white"
                  style={{ color: '#111827', backgroundColor: '#ffffff' }}
                >
                  <option value="insight">Insight</option>
                  <option value="thought-leadership">Thought Leadership</option>
                  <option value="strategy">Strategy</option>
                  <option value="tutorial">How-To / Tutorial</option>
                  <option value="testimony">Testimony / Story</option>
                  <option value="product">Product / Launch</option>
                  <option value="scripture">Scripture / Devotional</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">Tags (comma-separated)</label>
                <input
                  type="text"
                  name="tags"
                  value={formData.tags}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900 bg-white"
                  style={{ color: '#111827', backgroundColor: '#ffffff' }}
                  placeholder="faith, digital transformation, A.R.T. Energi"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">Hero Image (Title Image)</label>
              <div className="flex space-x-3">
                <input
                  type="text"
                  name="image"
                  value={formData.image}
                  onChange={handleInputChange}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900 bg-white"
                  style={{ color: '#111827', backgroundColor: '#ffffff' }}
                  placeholder="Image URL or upload below..."
                />
                <input
                  type="file"
                  ref={heroImageInputRef}
                  onChange={handleHeroImageUpload}
                  accept="image/*"
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => heroImageInputRef.current?.click()}
                  disabled={uploading}
                  className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                >
                  {uploading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      <span>Uploading...</span>
                    </>
                  ) : (
                    <>
                      <Upload size={18} />
                      <span>Upload</span>
                    </>
                  )}
                </button>
              </div>
              {formData.image && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600 mb-2">Preview:</p>
                  <img 
                    src={formData.image} 
                    alt="Hero preview" 
                    className="max-h-32 rounded-lg object-cover"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">Excerpt (Brief Summary)</label>
              <textarea
                name="excerpt"
                value={formData.excerpt}
                onChange={handleInputChange}
                rows="2"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900 bg-white"
                style={{ color: '#111827', backgroundColor: '#ffffff' }}
                placeholder="Brief 1-2 sentence summary..."
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-semibold text-gray-800">Content (HTML)</label>
                <div className="flex items-center space-x-2">
                  <input
                    type="file"
                    ref={contentImageInputRef}
                    onChange={handleContentImageUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => contentImageInputRef.current?.click()}
                    disabled={uploadingContent}
                    className="flex items-center space-x-1 px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {uploadingContent ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        <span>Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Image size={14} />
                        <span>Add Image</span>
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowTemplates(!showTemplates)}
                    className="flex items-center space-x-1 px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    <Layout size={14} />
                    <span>Templates</span>
                    {showTemplates ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                  <button
                    type="button"
                    onClick={runSpellcheck}
                    disabled={checkingSpell}
                    className="flex items-center space-x-1 px-3 py-1.5 bg-yellow-500 text-white text-sm rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50"
                    data-testid="run-spellcheck-btn"
                  >
                    {checkingSpell ? <Loader2 size={14} className="animate-spin" /> : <SpellCheck size={14} />}
                    <span>Spellcheck</span>
                  </button>
                </div>
              </div>

              {/* Spellcheck Panel (Refine flow) */}
              {(spellScore !== null || spellErrors.length > 0) && (
                <div className="mb-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200" data-testid="spellcheck-panel">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-yellow-800 flex items-center space-x-2">
                      <SpellCheck size={16} />
                      <span>Spelling — {spellErrors.length} flag(s){spellScore !== null ? ` · score ${spellScore}%` : ''}</span>
                    </h4>
                    <button type="button" onClick={runSpellcheck} className="text-xs text-yellow-700 hover:text-yellow-800 flex items-center gap-1">
                      <RefreshCw size={12} /> Re-check
                    </button>
                  </div>
                  {spellErrors.length === 0 ? (
                    <p className="text-sm text-green-700">No spelling flags — clean! ✓</p>
                  ) : (
                    <div className="space-y-2 max-h-72 overflow-y-auto">
                      {spellErrors.map((err) => (
                        <div key={err.word} className="p-2.5 bg-white rounded-lg border border-yellow-100" data-testid={`spell-row-${err.word}`}>
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <span className="font-mono text-sm text-red-600 font-semibold">{err.word}</span>
                            <div className="flex items-center gap-1.5">
                              <button type="button" onClick={() => addToDict(err.word)} className="text-[11px] px-2 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200" title="Add to Brand Dictionary (intentional spelling/acronym)">+ Dict</button>
                              <button type="button" onClick={() => ignoreSpell(err.word)} className="text-[11px] px-2 py-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200">Ignore</button>
                            </div>
                          </div>
                          {(err.suggestions && err.suggestions.length > 0) && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {err.suggestions.slice(0, 5).map((sug) => (
                                <button key={sug} type="button" onClick={() => applySpellFix(err.word, sug)}
                                  className="text-[11px] px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
                                  data-testid={`spell-fix-${err.word}-${sug}`}>
                                  {sug}
                                </button>
                              ))}
                            </div>
                          )}
                          <div className="flex items-center gap-1.5 mt-2">
                            <input
                              type="text"
                              value={manualFix[err.word] || ''}
                              onChange={(e) => setManualFix(prev => ({ ...prev, [err.word]: e.target.value }))}
                              placeholder="manual correction…"
                              className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded text-gray-900 bg-white"
                              data-testid={`spell-manual-${err.word}`}
                            />
                            <button type="button" onClick={() => applySpellFix(err.word, manualFix[err.word])}
                              className="text-[11px] px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600">Apply</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Templates Panel */}
              {showTemplates && (
                <div className="mb-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <h4 className="text-sm font-semibold text-purple-800 mb-3 flex items-center space-x-2">
                    <Layout size={16} />
                    <span>Insert Template ({templates.length} available)</span>
                  </h4>
                  {templates.length === 0 ? (
                    <p className="text-sm text-gray-600">No templates found. Create templates in the Template Manager.</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                      {templates.map((template) => (
                        <div key={template.id} className="flex items-center space-x-1">
                          <button
                            type="button"
                            onClick={() => addTemplateToContent(template.name, template.content)}
                            className="flex-1 text-left px-3 py-2 bg-white hover:bg-purple-100 rounded-lg text-sm text-purple-700 border border-purple-200 transition-colors truncate"
                            title={template.name}
                          >
                            <Plus size={12} className="inline mr-1" />
                            {template.name}
                          </button>
                          <button
                            type="button"
                            onClick={() => copyTemplateToClipboard(template.id, template.content)}
                            className="p-2 hover:bg-white rounded-lg transition-colors border border-purple-200"
                            title="Copy to clipboard"
                          >
                            {copiedTemplate === template.id ? (
                              <Check size={14} className="text-green-500" />
                            ) : (
                              <Copy size={14} className="text-purple-400" />
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <textarea
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                rows="12"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-sm text-gray-900 bg-white"
                style={{ color: '#111827', backgroundColor: '#ffffff' }}
                placeholder="<h2>Section Title</h2><p>Content here...</p>"
              />
              <p className="text-xs text-gray-600 mt-1">Use HTML tags: &lt;h2&gt;, &lt;h3&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;. Click "Add Image to Content" to upload and insert images.</p>
            </div>

            <div className="flex space-x-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-orange-500 text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
              >
                <Save size={20} />
                <span>{loading ? 'Saving...' : editingArticle ? 'Update Article' : 'Create Article'}</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingArticle(null);
                }}
                className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ArticlesManager;
